import { useState, useEffect, useCallback } from "react";

/**
 * Shared hook for TV display settings.
 * Manages view mode, theme, zoom, and countdown overlay settings.
 * Settings are persisted to localStorage for cross-session persistence.
 */

// Available theme colors (matching ThemeContext)
export const THEME_COLORS = {
  Gold: "#bf9a2f",
  Green: "#4CAF50",
  Purple: "#6A5ACD",
  "Ocean Blue": "#1E90FF",
  Orange: "#FFA07A",
  Teal: "#00FFFF",
};

// View modes
export const VIEW_MODES = {
  TODAY: "today",
  MULTIDAY: "multiday",
  EVENTS: "events",
  IMAGES: "images",
};

// Theme modes
export const THEME_MODES = {
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
  AUTO: "auto", // Auto switches based on sunrise/maghrib
};

// Storage keys
// Maximum number of custom colors allowed
const MAX_CUSTOM_COLORS = 7;

const STORAGE_KEYS = {
  VIEW_MODE: "tv-view-mode",
  SHOW_TOMORROW: "tv-show-tomorrow",
  THEME_MODE: "tv-theme-mode",
  PRIMARY_COLOR: "tv-primary-color",
  CUSTOM_COLORS: "tv-custom-colors",
  ZOOM_LEVEL: "tv-zoom-level",
  ADHAN_ENABLED: "tv-adhan-enabled",
  IQAMA_ENABLED: "tv-iqama-enabled",
  ADHAN_COUNTDOWN_MINUTES: "tv-adhan-countdown-minutes",
  IQAMA_COUNTDOWN_MINUTES: "tv-iqama-countdown-minutes",
  ADHAN_SCREEN_DURATION_MINUTES: "tv-adhan-screen-duration-minutes",
  IQAMA_SCREEN_DURATION_MINUTES: "tv-iqama-screen-duration-minutes",
  // Jummah silence screen settings
  JUMMAH_SILENCE_ENABLED: "tv-jummah-silence-enabled",
  JUMMAH_SILENCE_DURATION_MINUTES: "tv-jummah-silence-duration-minutes",
  // Forbidden prayer time screen settings (after sunrise)
  FORBIDDEN_SCREEN_ENABLED: "tv-forbidden-screen-enabled",
  FORBIDDEN_SCREEN_DURATION_MINUTES: "tv-forbidden-screen-duration-minutes",
  // Tomorrow changes banner (PrayerTimesView only)
  TOMORROW_CHANGES_BANNER: "tv-tomorrow-changes-banner",
};

// Default settings
const DEFAULT_SETTINGS = {
  viewMode: VIEW_MODES.MULTIDAY,
  showTomorrow: true, // For events/images view
  themeMode: THEME_MODES.AUTO,
  primaryColor: THEME_COLORS.Gold,
  customColor: null, // Custom hex color if not using preset
  customColors: [], // Array of saved custom colors
  zoomLevel: 1.0,
  adhanEnabled: true,
  iqamaEnabled: true,
  adhanCountdownMinutes: 1, // Minutes before adhan to start countdown
  iqamaCountdownMinutes: 1, // Minutes before iqama to start countdown
  adhanScreenDurationMinutes: 2, // How long to show adhan screen after countdown
  iqamaScreenDurationMinutes: 15, // How long to show iqama screen after countdown
  // Jummah silence screen - shows during khutbah/after Jummah
  jummahSilenceEnabled: false,
  jummahSilenceDurationMinutes: 30, // Duration in minutes (up to 60)
  // Forbidden prayer time screen - shows after sunrise
  forbiddenScreenEnabled: false,
  forbiddenScreenDurationMinutes: 20, // 10, 15, 20, or 30 minutes
  // Tomorrow changes banner (PrayerTimesView only)
  tomorrowChangesBanner: true, // Enabled by default
};

/**
 * Hook for managing TV display settings
 * @param {Object} options
 * @param {Object} options.globalTheme - Global theme from ThemeContext (for default color)
 * @param {string} options.globalAppearance - Global appearance from ThemeContext
 * @returns {Object} Settings state and controls
 */
export function useTVSettings({ globalTheme, globalAppearance } = {}) {
  // Load initial state from localStorage or use defaults
  const loadSetting = (key, defaultValue, parser = (v) => v) => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        return parser(stored);
      }
    } catch (e) {
      console.warn(`Failed to load setting ${key}:`, e);
    }
    return defaultValue;
  };

  // View settings
  const [viewMode, setViewModeState] = useState(() =>
    loadSetting(STORAGE_KEYS.VIEW_MODE, DEFAULT_SETTINGS.viewMode)
  );
  const [showTomorrow, setShowTomorrowState] = useState(() =>
    loadSetting(STORAGE_KEYS.SHOW_TOMORROW, DEFAULT_SETTINGS.showTomorrow, (v) => v === "true")
  );

  // Theme settings
  const [themeMode, setThemeModeState] = useState(() =>
    loadSetting(STORAGE_KEYS.THEME_MODE, DEFAULT_SETTINGS.themeMode)
  );
  const [primaryColor, setPrimaryColorState] = useState(() =>
    loadSetting(STORAGE_KEYS.PRIMARY_COLOR, globalTheme?.colors?.primary || DEFAULT_SETTINGS.primaryColor)
  );
  const [customColor, setCustomColorState] = useState(null);
  const [customColors, setCustomColorsState] = useState(() =>
    loadSetting(STORAGE_KEYS.CUSTOM_COLORS, DEFAULT_SETTINGS.customColors, (v) => {
      try {
        const parsed = JSON.parse(v);
        return Array.isArray(parsed) ? parsed.slice(0, MAX_CUSTOM_COLORS) : [];
      } catch {
        return [];
      }
    })
  );

  // Zoom settings
  const [zoomLevel, setZoomLevelState] = useState(() =>
    loadSetting(STORAGE_KEYS.ZOOM_LEVEL, DEFAULT_SETTINGS.zoomLevel, parseFloat)
  );

  // Countdown overlay settings
  const [adhanEnabled, setAdhanEnabledState] = useState(() =>
    loadSetting(STORAGE_KEYS.ADHAN_ENABLED, DEFAULT_SETTINGS.adhanEnabled, (v) => v === "true")
  );
  const [iqamaEnabled, setIqamaEnabledState] = useState(() =>
    loadSetting(STORAGE_KEYS.IQAMA_ENABLED, DEFAULT_SETTINGS.iqamaEnabled, (v) => v === "true")
  );
  const [adhanCountdownMinutes, setAdhanCountdownMinutesState] = useState(() =>
    loadSetting(STORAGE_KEYS.ADHAN_COUNTDOWN_MINUTES, DEFAULT_SETTINGS.adhanCountdownMinutes, parseInt)
  );
  const [iqamaCountdownMinutes, setIqamaCountdownMinutesState] = useState(() =>
    loadSetting(STORAGE_KEYS.IQAMA_COUNTDOWN_MINUTES, DEFAULT_SETTINGS.iqamaCountdownMinutes, parseInt)
  );
  const [adhanScreenDurationMinutes, setAdhanScreenDurationMinutesState] = useState(() =>
    loadSetting(STORAGE_KEYS.ADHAN_SCREEN_DURATION_MINUTES, DEFAULT_SETTINGS.adhanScreenDurationMinutes, parseInt)
  );
  const [iqamaScreenDurationMinutes, setIqamaScreenDurationMinutesState] = useState(() =>
    loadSetting(STORAGE_KEYS.IQAMA_SCREEN_DURATION_MINUTES, DEFAULT_SETTINGS.iqamaScreenDurationMinutes, parseInt)
  );

  // Jummah silence screen settings
  const [jummahSilenceEnabled, setJummahSilenceEnabledState] = useState(() =>
    loadSetting(STORAGE_KEYS.JUMMAH_SILENCE_ENABLED, DEFAULT_SETTINGS.jummahSilenceEnabled, (v) => v === "true")
  );
  const [jummahSilenceDurationMinutes, setJummahSilenceDurationMinutesState] = useState(() =>
    loadSetting(STORAGE_KEYS.JUMMAH_SILENCE_DURATION_MINUTES, DEFAULT_SETTINGS.jummahSilenceDurationMinutes, parseInt)
  );

  // Forbidden prayer time screen settings (after sunrise)
  const [forbiddenScreenEnabled, setForbiddenScreenEnabledState] = useState(() =>
    loadSetting(STORAGE_KEYS.FORBIDDEN_SCREEN_ENABLED, DEFAULT_SETTINGS.forbiddenScreenEnabled, (v) => v === "true")
  );
  const [forbiddenScreenDurationMinutes, setForbiddenScreenDurationMinutesState] = useState(() =>
    loadSetting(STORAGE_KEYS.FORBIDDEN_SCREEN_DURATION_MINUTES, DEFAULT_SETTINGS.forbiddenScreenDurationMinutes, parseInt)
  );

  // Tomorrow changes banner (PrayerTimesView only)
  const [tomorrowChangesBanner, setTomorrowChangesBannerState] = useState(() =>
    loadSetting(STORAGE_KEYS.TOMORROW_CHANGES_BANNER, DEFAULT_SETTINGS.tomorrowChangesBanner, (v) => v === "true")
  );

  // Computed local appearance based on theme mode
  const [localAppearance, setLocalAppearanceState] = useState(() => {
    const mode = loadSetting(STORAGE_KEYS.THEME_MODE, DEFAULT_SETTINGS.themeMode);
    let appearance = "light";
    if (mode === THEME_MODES.LIGHT) appearance = "light";
    else if (mode === THEME_MODES.DARK) appearance = "dark";
    else if (mode === THEME_MODES.SYSTEM) {
      const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
      appearance = prefersDark ? "dark" : "light";
    }
    // For auto mode, default to light (will be updated by sunrise/maghrib logic)
    return appearance;
  });
  
  // Wrapper for setLocalAppearance
  const setLocalAppearance = useCallback((value) => {
    setLocalAppearanceState(value);
  }, []);

  // Persist settings to localStorage
  const saveSetting = (key, value) => {
    try {
      localStorage.setItem(key, String(value));
    } catch (e) {
      console.warn(`Failed to save setting ${key}:`, e);
    }
  };

  // Setting setters with persistence
  const setViewMode = useCallback((value) => {
    setViewModeState(value);
    saveSetting(STORAGE_KEYS.VIEW_MODE, value);
  }, []);

  const setShowTomorrow = useCallback((value) => {
    setShowTomorrowState(value);
    saveSetting(STORAGE_KEYS.SHOW_TOMORROW, value);
  }, []);

  const setThemeMode = useCallback((value) => {
    setThemeModeState(value);
    saveSetting(STORAGE_KEYS.THEME_MODE, value);

    // Update local appearance based on new mode
    if (value === THEME_MODES.LIGHT) {
      setLocalAppearance("light");
    } else if (value === THEME_MODES.DARK) {
      setLocalAppearance("dark");
    } else if (value === THEME_MODES.SYSTEM) {
      const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
      setLocalAppearance(prefersDark ? "dark" : "light");
    }
    // For AUTO mode, let the auto-theme logic in the component handle it
  }, []);

  const setPrimaryColor = useCallback((value) => {
    setPrimaryColorState(value);
    saveSetting(STORAGE_KEYS.PRIMARY_COLOR, value);
    setCustomColorState(null); // Clear custom color when selecting preset
  }, []);

  const setCustomColor = useCallback((value) => {
    if (value) {
      setCustomColorState(value);
      setPrimaryColorState(value);
      saveSetting(STORAGE_KEYS.PRIMARY_COLOR, value);
      
      // Add to custom colors if not a preset
      const normalizedValue = value.toUpperCase();
      const isPreset = Object.values(THEME_COLORS).some(
        (c) => c.toUpperCase() === normalizedValue
      );
      if (!isPreset) {
        setCustomColorsState((prev) => {
          if (prev.some((c) => c.toUpperCase() === normalizedValue)) {
            return prev;
          }
          const newColors = prev.length >= MAX_CUSTOM_COLORS
            ? [...prev.slice(1), value]
            : [...prev, value];
          saveSetting(STORAGE_KEYS.CUSTOM_COLORS, JSON.stringify(newColors));
          return newColors;
        });
      }
    }
  }, []);

  const addCustomColor = useCallback((color) => {
    const normalizedColor = color.toUpperCase();
    const isPreset = Object.values(THEME_COLORS).some(
      (c) => c.toUpperCase() === normalizedColor
    );
    if (isPreset) return;

    setCustomColorsState((prev) => {
      if (prev.some((c) => c.toUpperCase() === normalizedColor)) {
        return prev;
      }
      const newColors = prev.length >= MAX_CUSTOM_COLORS
        ? [...prev.slice(1), color]
        : [...prev, color];
      saveSetting(STORAGE_KEYS.CUSTOM_COLORS, JSON.stringify(newColors));
      return newColors;
    });
  }, []);

  const removeCustomColor = useCallback((colorToRemove) => {
    setCustomColorsState((prev) => {
      const newColors = prev.filter(
        (c) => c.toUpperCase() !== colorToRemove.toUpperCase()
      );
      saveSetting(STORAGE_KEYS.CUSTOM_COLORS, JSON.stringify(newColors));
      return newColors;
    });
  }, []);

  const clearCustomColors = useCallback(() => {
    setCustomColorsState([]);
    saveSetting(STORAGE_KEYS.CUSTOM_COLORS, JSON.stringify([]));
  }, []);

  const setZoomLevel = useCallback((value) => {
    const clamped = Math.max(0.5, Math.min(2.0, value));
    setZoomLevelState(clamped);
    saveSetting(STORAGE_KEYS.ZOOM_LEVEL, clamped);
  }, []);

  const setAdhanEnabled = useCallback((value) => {
    setAdhanEnabledState(value);
    saveSetting(STORAGE_KEYS.ADHAN_ENABLED, value);
  }, []);

  const setIqamaEnabled = useCallback((value) => {
    setIqamaEnabledState(value);
    saveSetting(STORAGE_KEYS.IQAMA_ENABLED, value);
  }, []);

  const setAdhanCountdownMinutes = useCallback((value) => {
    const clamped = Math.max(0, Math.min(10, value));
    setAdhanCountdownMinutesState(clamped);
    saveSetting(STORAGE_KEYS.ADHAN_COUNTDOWN_MINUTES, clamped);
  }, []);

  const setIqamaCountdownMinutes = useCallback((value) => {
    const clamped = Math.max(0, Math.min(10, value));
    setIqamaCountdownMinutesState(clamped);
    saveSetting(STORAGE_KEYS.IQAMA_COUNTDOWN_MINUTES, clamped);
  }, []);

  const setAdhanScreenDurationMinutes = useCallback((value) => {
    const clamped = Math.max(0, Math.min(5, value));
    setAdhanScreenDurationMinutesState(clamped);
    saveSetting(STORAGE_KEYS.ADHAN_SCREEN_DURATION_MINUTES, clamped);
  }, []);

  const setIqamaScreenDurationMinutes = useCallback((value) => {
    const clamped = Math.max(0, Math.min(30, value));
    setIqamaScreenDurationMinutesState(clamped);
    saveSetting(STORAGE_KEYS.IQAMA_SCREEN_DURATION_MINUTES, clamped);
  }, []);

  // Jummah silence screen setters
  const setJummahSilenceEnabled = useCallback((value) => {
    setJummahSilenceEnabledState(value);
    saveSetting(STORAGE_KEYS.JUMMAH_SILENCE_ENABLED, value);
  }, []);

  const setJummahSilenceDurationMinutes = useCallback((value) => {
    const clamped = Math.max(1, Math.min(60, value));
    setJummahSilenceDurationMinutesState(clamped);
    saveSetting(STORAGE_KEYS.JUMMAH_SILENCE_DURATION_MINUTES, clamped);
  }, []);

  // Forbidden screen setters (allowed values: 10, 15, 20, 30)
  const setForbiddenScreenEnabled = useCallback((value) => {
    setForbiddenScreenEnabledState(value);
    saveSetting(STORAGE_KEYS.FORBIDDEN_SCREEN_ENABLED, value);
  }, []);

  const setForbiddenScreenDurationMinutes = useCallback((value) => {
    // Only allow specific values: 10, 15, 20, 30
    const allowed = [10, 15, 20, 30];
    const closest = allowed.reduce((prev, curr) => 
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
    setForbiddenScreenDurationMinutesState(closest);
    saveSetting(STORAGE_KEYS.FORBIDDEN_SCREEN_DURATION_MINUTES, closest);
  }, []);

  // Tomorrow changes banner setter
  const setTomorrowChangesBanner = useCallback((value) => {
    setTomorrowChangesBannerState(value);
    saveSetting(STORAGE_KEYS.TOMORROW_CHANGES_BANNER, value);
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode !== THEME_MODES.SYSTEM) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      setLocalAppearance(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themeMode]);

  // Reset to defaults (but preserve current viewMode)
  const resetToDefaults = useCallback(() => {
    // Don't reset viewMode - keep current page/view
    setShowTomorrow(DEFAULT_SETTINGS.showTomorrow);
    setThemeMode(DEFAULT_SETTINGS.themeMode);
    setPrimaryColor(globalTheme?.colors?.primary || DEFAULT_SETTINGS.primaryColor);
    setZoomLevel(DEFAULT_SETTINGS.zoomLevel);
    setAdhanEnabled(DEFAULT_SETTINGS.adhanEnabled);
    setIqamaEnabled(DEFAULT_SETTINGS.iqamaEnabled);
    setAdhanCountdownMinutes(DEFAULT_SETTINGS.adhanCountdownMinutes);
    setIqamaCountdownMinutes(DEFAULT_SETTINGS.iqamaCountdownMinutes);
    setAdhanScreenDurationMinutes(DEFAULT_SETTINGS.adhanScreenDurationMinutes);
    setIqamaScreenDurationMinutes(DEFAULT_SETTINGS.iqamaScreenDurationMinutes);
    setJummahSilenceEnabled(DEFAULT_SETTINGS.jummahSilenceEnabled);
    setJummahSilenceDurationMinutes(DEFAULT_SETTINGS.jummahSilenceDurationMinutes);
    setForbiddenScreenEnabled(DEFAULT_SETTINGS.forbiddenScreenEnabled);
    setForbiddenScreenDurationMinutes(DEFAULT_SETTINGS.forbiddenScreenDurationMinutes);
    setTomorrowChangesBanner(DEFAULT_SETTINGS.tomorrowChangesBanner);
  }, [
    globalTheme,
    setShowTomorrow,
    setThemeMode,
    setPrimaryColor,
    setZoomLevel,
    setAdhanEnabled,
    setIqamaEnabled,
    setAdhanCountdownMinutes,
    setIqamaCountdownMinutes,
    setAdhanScreenDurationMinutes,
    setIqamaScreenDurationMinutes,
    setJummahSilenceEnabled,
    setJummahSilenceDurationMinutes,
    setForbiddenScreenEnabled,
    setForbiddenScreenDurationMinutes,
    setTomorrowChangesBanner,
  ]);

  // Check if color is a preset
  const isPresetColor = Object.values(THEME_COLORS).includes(primaryColor);
  const colorName = isPresetColor
    ? Object.entries(THEME_COLORS).find(([, c]) => c === primaryColor)?.[0]
    : "Custom";

  return {
    // View settings
    viewMode,
    setViewMode,
    showTomorrow,
    setShowTomorrow,

    // Theme settings
    themeMode,
    setThemeMode,
    primaryColor,
    setPrimaryColor,
    customColor,
    setCustomColor,
    localAppearance,
    setLocalAppearance, // For auto theme logic
    isDark: localAppearance === "dark",
    colorName,
    isPresetColor,

    // Custom colors management
    customColors,
    addCustomColor,
    removeCustomColor,
    clearCustomColors,
    MAX_CUSTOM_COLORS,

    // Zoom settings
    zoomLevel,
    setZoomLevel,

    // Countdown settings
    adhanEnabled,
    setAdhanEnabled,
    iqamaEnabled,
    setIqamaEnabled,
    adhanCountdownMinutes,
    setAdhanCountdownMinutes,
    iqamaCountdownMinutes,
    setIqamaCountdownMinutes,
    adhanScreenDurationMinutes,
    setAdhanScreenDurationMinutes,
    iqamaScreenDurationMinutes,
    setIqamaScreenDurationMinutes,

    // Jummah silence screen settings
    jummahSilenceEnabled,
    setJummahSilenceEnabled,
    jummahSilenceDurationMinutes,
    setJummahSilenceDurationMinutes,

    // Forbidden prayer time screen settings (after sunrise)
    forbiddenScreenEnabled,
    setForbiddenScreenEnabled,
    forbiddenScreenDurationMinutes,
    setForbiddenScreenDurationMinutes,

    // Tomorrow changes banner (PrayerTimesView only)
    tomorrowChangesBanner,
    setTomorrowChangesBanner,

    // Utilities
    resetToDefaults,
    THEME_COLORS,
    VIEW_MODES,
    THEME_MODES,
  };
}

export default useTVSettings;
